import { useEffect, useMemo, useState } from 'react';
import { CollectionContextProvider } from '@/features/events/contexts/CollectionContext/CollectionContextProvider';
import type { DecoratedExperienceEvent } from '@/features/events/types';
import { SingleEventExportForm } from '@/features/interactions/addToCalendar/components/SingleEventExportForm';
import { UpdatesModal } from '@/features/interactions/addToCalendar/components/UpdatesModal';
import {
  EVENT_EXPORT_PLATFORMS,
  getDestination,
  isDrawerPlatform,
  isFeedPlatform,
  isMultiExportPlatform,
  isPerEventPlatform,
} from '@/features/interactions/addToCalendar/destinations/registry';
import { useWeblinkAdd } from '@/features/interactions/addToCalendar/hooks/useWeblinkAdd';
import { useExportPlatforms } from '@/features/interactions/lib/useExportPlatforms';
import {
  useSelectModeStore,
  useSelectedEventIds,
} from '@/features/interactions/multiExport/hooks/useSelectModeStore';
import { SubscribeFeedModal } from '@/features/interactions/subscribe/SubscribeFeedModal';
import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';
import { PlatformTypes } from '@/types/gql.generated';
import { Box, Button, Divider, Flex, Text, useDisclosure } from '@/ui';
import { getName } from '@/utils/platformLib';
import { usePageExperienceContext } from '../../contexts/PageExperienceContext';
import { DestinationActions } from './DestinationActions';
import { DestinationDescription } from './DestinationDescription';
import { ExportEventRow, type RowMode } from './ExportEventRow';
import { ExportPlatformPicker } from './ExportPlatformPicker';
import { ExportSidebarHeader } from './ExportSidebarHeader';
import { FeedExportView } from './FeedExportView';
import {
  useExportableEvents,
  type ExportableEvent,
} from './useExportableEvents';
import { usePendingSubscriptionSync } from './usePendingSubscriptionSync';

type Props = {
  onClose: () => void;
};

type InnerProps = Props & {
  rows: ExportableEvent[];
  rangeLabel: string | null;
};

// Destinations reuse the canonical multi-export groupings (EVENT_EXPORT_PLATFORMS):
//  - MULTI_EXPORT (Agenda Hero, Google, Outlook) — bulk-select and write the
//    subset. Outlook is flag-gated: useExportPlatforms swaps it in for the
//    Outlook/Office weblinks when the flag is on, and strips it when off.
//  - PER_EVENT (Apple, Outlook, Office) — per-event server weblink (one click
//    per row opens that platform's add-to-calendar link).
//  - DRAWER (Email) — per-event invite compose, expanded inside the row's card.

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Text textStyle="ahLabel">{children}</Text>
);

const BodyInner = ({ rangeLabel, rows, onClose }: InnerProps) => {
  const { experience, readOnly } = usePageExperienceContext();
  const isMobile = useIsMobileBreakpoint();
  const { addToWeblink } = useWeblinkAdd({
    experienceId: experience.id,
    mode: readOnly ? 'view' : 'edit',
  });

  const [selectedPlatform, setSelectedPlatform] =
    useState<PlatformTypes | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [composeEventId, setComposeEventId] = useState<string | null>(null);
  // The row that just sent an invite anchors the one-shot confetti burst.
  const [celebratedEventId, setCelebratedEventId] = useState<string | null>(
    null
  );
  const [needsDestination, setNeedsDestination] = useState(false);
  const feedModal = useDisclosure();

  const enterSelectMode = useSelectModeStore((state) => state.enterSelectMode);
  const exitSelectMode = useSelectModeStore((state) => state.exitSelectMode);
  const setSelectedEvents = useSelectModeStore(
    (state) => state.setSelectedEvents
  );
  const selectedEventIds = useSelectedEventIds();
  const isSelectMode = useSelectModeStore((state) => state.isSelectMode);
  const selectionExperienceId = useSelectModeStore(
    (state) => state.experienceId
  );
  const selectionPlatform = useSelectModeStore((state) => state.platform);
  const selectionSurface = useSelectModeStore((state) => state.surface);

  const allIds = useMemo(() => rows.map((row) => row.event.id), [rows]);

  // Persisted selections can include rows outside the current range. Scope the
  // ids here so displayed counts and exported events stay aligned.
  const selectedInRangeIds = useMemo(() => {
    const selected = new Set(selectedEventIds);
    return allIds.filter((id) => selected.has(id));
  }, [selectedEventIds, allIds]);

  const platforms = useExportPlatforms(EVENT_EXPORT_PLATFORMS);

  // The flag reads false while it loads, so the Microsoft weblinks are briefly
  // offered before being replaced. A selection made in that window would
  // otherwise outlive its own chip and keep the sidebar in per-event weblink mode
  // for a destination no longer on offer.
  useEffect(() => {
    if (selectedPlatform && !platforms.includes(selectedPlatform)) {
      setSelectedPlatform(null);
      setIsDrawerOpen(false);
      exitSelectMode();
    }
  }, [platforms, selectedPlatform, exitSelectMode]);

  // Restore the destination the persisted selection was made for — a reload
  // mid-flow (most importantly the Google/Outlook OAuth connect round trip)
  // wipes this component's state while the selection rides the store. Waits
  // for the platform to be on offer, so a flag-gated destination isn't
  // restored into the flag's loading window (the effect above would wipe the
  // selection along with the chip).
  useEffect(() => {
    if (
      !selectedPlatform &&
      isSelectMode &&
      selectionExperienceId === experience.id &&
      selectionPlatform &&
      platforms.includes(selectionPlatform)
    ) {
      setSelectedPlatform(selectionPlatform);
    }
  }, [
    selectedPlatform,
    isSelectMode,
    selectionExperienceId,
    selectionPlatform,
    platforms,
    experience.id,
  ]);

  const handlePlatformChange = (dest: PlatformTypes) => {
    setIsDrawerOpen(false);
    setComposeEventId(null);
    setCelebratedEventId(null);
    setNeedsDestination(false);
    const next = dest === selectedPlatform ? null : dest;
    setSelectedPlatform(next);
    // Only the bulk-export integrations use select mode. Claimed for this
    // surface so the page's event cards stay out of checkbox mode.
    if (next && isMultiExportPlatform(next)) {
      enterSelectMode(experience.id, next, 'sidebar');
      // Scoped to this surface: the legacy bar drives the same global store,
      // and picking a per-row destination here must not wipe its selection.
    } else if (isSelectMode && selectionSurface === 'sidebar') {
      exitSelectMode();
    }
  };

  const platform = selectedPlatform;
  const isIntegration = !!platform && isMultiExportPlatform(platform);
  const isWeblink = !!platform && isPerEventPlatform(platform);
  const isEmail = !!platform && isDrawerPlatform(platform);
  const isFeed = !!platform && isFeedPlatform(platform);
  const showChecks = isIntegration;
  const rowMode: RowMode = isIntegration
    ? 'select'
    : isWeblink
      ? 'weblink'
      : isEmail
        ? 'email'
        : 'readonly';

  const selectAll = () => setSelectedEvents(allIds);
  const deselectAll = () => setSelectedEvents([]);

  // Per-event weblink add — the same shared flow as WeblinkInteractionMenu
  // (the card-footer control): analytics, UpdatesModal gate, link open, and the
  // persisted local record the row's "Added" badge reads.
  const handleWeblinkAdd = (event: DecoratedExperienceEvent) => {
    if (!platform) return;
    addToWeblink(event, platform);
  };

  const handleComposeToggle = (eventId: string) => {
    setComposeEventId((current) => (current === eventId ? null : eventId));
  };

  // Primary action for the chosen destination — below the chips on desktop,
  // pinned to the bottom on mobile. Only rendered for integrations (feed and
  // weblink have their own controls), so guarded by callers, not here.
  const actions = (
    <DestinationActions
      isDrawerOpen={isDrawerOpen}
      platform={platform}
      selectedEventIds={selectedInRangeIds}
      setIsDrawerOpen={setIsDrawerOpen}
      onDeselectAll={deselectAll}
      onSelectAll={selectAll}
    />
  );

  return (
    <Flex direction="column" h="100%" minH={0}>
      <ExportSidebarHeader onClose={onClose} />

      {/* Platform picker */}
      <Box flexShrink={0} pb={4} px={5}>
        <Box mb={2.5}>
          <SectionLabel>Choose a calendar</SectionLabel>
        </Box>
        <Box
          borderRadius="ah-lg"
          boxShadow={needsDestination ? 'ah-ring-active' : undefined}
          transition="box-shadow .15s"
        >
          <ExportPlatformPicker
            platforms={platforms}
            selectedPlatform={selectedPlatform}
            onChange={handlePlatformChange}
          />
        </Box>
        {needsDestination && !selectedPlatform && (
          <Text
            color="brand.500"
            fontSize="ah-status"
            fontWeight={600}
            mt={2}
            role="status"
          >
            Choose a calendar first, then pick your events.
          </Text>
        )}
        {selectedPlatform && (
          <DestinationDescription
            isWeblink={isWeblink}
            platform={platform}
            onOpenFeed={feedModal.onOpen}
            onSelectEmail={
              platforms.includes(PlatformTypes.Email)
                ? () => handlePlatformChange(PlatformTypes.Email)
                : undefined
            }
          />
        )}
      </Box>

      {/* Desktop: the primary action sits directly below the chips. Feed shows
          its subscribe card, and weblink/email are per-row actions, so none of
          them has one. */}
      {!isMobile && selectedPlatform && !isFeed && !isWeblink && !isEmail && (
        <Box flexShrink={0} pb={4} px={5}>
          {actions}
        </Box>
      )}

      <Divider />

      {/* Feed: subscribe to the whole schedule — no event picking, so the
          event list is replaced by the subscribe card. */}
      {isFeed ? (
        <Box flex="1" minH={0} overflowY="auto" pb={4} pt={4} px={5}>
          {/* Whole-schedule feed lives on the parent planner (same experience
              SubscribeFeedModal subscribes to), not the active child view. */}
          <FeedExportView experienceId={experience.id} />
        </Box>
      ) : (
        <>
          {/* Events header */}
          {rows.length > 0 && (
            <Flex
              align="center"
              flexShrink={0}
              justify="space-between"
              px={5}
              py={3}
            >
              <SectionLabel>
                {rangeLabel ? `Events ${rangeLabel}` : 'Events'}
              </SectionLabel>
              {showChecks && selectedInRangeIds.length > 0 && (
                <Button
                  color="brand.500"
                  fontSize="ah-label"
                  fontWeight={700}
                  letterSpacing="ah-label"
                  size="xs"
                  textTransform="uppercase"
                  variant="link"
                  onClick={deselectAll}
                >
                  Cancel
                </Button>
              )}
            </Flex>
          )}

          {/* Scrollable event list */}
          <Box flex="1" minH={0} overflowY="auto" pb={4} px={5}>
            {rows.length === 0 ? (
              <Flex align="center" h="100%" justify="center" py={10}>
                <Text color="ink.muted" fontSize="ah-status" textAlign="center">
                  No events in the current date range.
                </Text>
              </Flex>
            ) : (
              <Flex direction="column" gap={2}>
                {rows.map((row) => {
                  const isComposeOpen =
                    isEmail && composeEventId === row.event.id;
                  return (
                    <ExportEventRow
                      isExpanded={isComposeOpen}
                      key={row.event.id}
                      mode={rowMode}
                      platform={platform}
                      row={row}
                      addLabel={
                        isWeblink && platform
                          ? `Add to ${getName(platform)}`
                          : isEmail && platform
                            ? getDestination(platform)?.addLabel
                            : undefined
                      }
                      showConfetti={
                        isEmail && celebratedEventId === row.event.id
                      }
                      onAdd={
                        isWeblink
                          ? () => handleWeblinkAdd(row.event)
                          : isEmail
                            ? () => handleComposeToggle(row.event.id)
                            : undefined
                      }
                      onRequestDestination={
                        platform ? undefined : () => setNeedsDestination(true)
                      }
                    >
                      {isComposeOpen && (
                        <SingleEventExportForm
                          eventItem={row.event}
                          platform={platform}
                          onClose={() => setComposeEventId(null)}
                          onSuccess={() => {
                            setComposeEventId(null);
                            setCelebratedEventId(row.event.id);
                          }}
                        />
                      )}
                    </ExportEventRow>
                  );
                })}
              </Flex>
            )}
          </Box>
        </>
      )}

      {/* Mobile: the primary action is pinned to the bottom. Caps its own
          height so a tall export drawer never starves the list above it. */}
      {isMobile && selectedPlatform && !isFeed && !isWeblink && !isEmail && (
        <Box
          borderTopColor="line.panel"
          borderTopStyle="solid"
          borderTopWidth="1px"
          flexShrink={0}
          maxH="55%"
          overflowY="auto"
          px={5}
          py={4}
        >
          {actions}
        </Box>
      )}

      <SubscribeFeedModal
        isOpen={feedModal.isOpen}
        onClose={feedModal.onClose}
      />
      {/* Store-driven; opening happens from handleWeblinkAdd for eligible
          per-event adds (mirrors MultiExportBar rendering it once). Targets the
          parent planner experience — the same one the weblink adds and feed
          subscribe to. */}
      <UpdatesModal experience={experience} />
    </Flex>
  );
};

export const ExportCalendarSidebarBody = ({ onClose }: Props) => {
  const { experience } = usePageExperienceContext();
  const { rows, rangeLabel } = useExportableEvents();
  const collectionEvents = useMemo(() => rows.map((r) => r.event), [rows]);
  usePendingSubscriptionSync();

  // Scope the reused export stack (useAddEvents / BulkExportDrawer read
  // useCollection()) to exactly the in-range series. Base on the parent planner
  // experience (mirrors ChildPlannerView): the planner's events belong to it,
  // and its real publishedSlug lets revalidate() invalidate the published-events
  // cache by slug as well as by id.
  return (
    <CollectionContextProvider base={{ experience }} events={collectionEvents}>
      <BodyInner rangeLabel={rangeLabel} rows={rows} onClose={onClose} />
    </CollectionContextProvider>
  );
};
